import React, { createContext, useState, useMemo, useEffect, useContext, type ReactNode } from 'react';
import cache from 'lscache';

import { useGetProcessesQuery } from '../../apis/ilink/queries';
import { usePlanningStateFilter } from '../../utils/hooks/usePlanningStateFilter';
import { type Intervention } from '../InterventionsPlanningResult/InterventionPlanningResult.type';
import type { FilterType } from '../Filters/filter.type';
import { getCurrentDate } from '../../utils/date/date-utils';
import { useGenerateInterventionBusinessTypes, useGenerateInterventionTypeFilter } from './interventionTypes.helper';
import { keyCacheInterventionType, KEY_CACHE_PROCESSES, keyCacheBusinessType } from './intervention-provider.const';

interface InterventionsProviderProps {
  processesReady: boolean;
  processes: FilterType[];
  interventionTypesFilter: FilterType[];
  interventionStatesFilter: FilterType[];
  businessTypesFilter: FilterType[];
  beginDate: Date;
  endDate?: Date;
  interventionIds: string[];
  interventions: Intervention[];
  customerFullName: string;
  setProcesses: (processes: FilterType[]) => void;
  setInterventionTypesFilter: (processes: FilterType[]) => void;
  setBusinessTypeTypesFilter: (processes: FilterType[]) => void;
  setInterventionStatesFilter: (processes: FilterType[]) => void;
  setBeginDate: React.Dispatch<React.SetStateAction<Date>>;
  setEndDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
  setInterventionIds: (interventionIds: string[]) => void;
  setInterventions: (interventions: Intervention[]) => void;
  setCustomerFullName: (customerFullName: string) => void;
}

const InterventionContext = createContext<InterventionsProviderProps>({} as any);

interface InterventionsProviderComponentProps {
  readonly children: ReactNode;
}

export const InterventionsProvider = ({ children }: InterventionsProviderComponentProps) => {
  const interventionTypeFilter = useGenerateInterventionTypeFilter();
  const businessTypeFilter = useGenerateInterventionBusinessTypes();
  const savedInterventionTypes: FilterType[] | null = cache.get(keyCacheInterventionType) || null;
  const savedBusinessTypeTypes: FilterType[] | null = cache.get(keyCacheBusinessType) || null;
  const [processes, setProcesses] = useState<FilterType[]>(JSON.parse(localStorage.getItem(KEY_CACHE_PROCESSES) ?? '[]'));

  const defaultInterventionTypes = savedInterventionTypes || interventionTypeFilter;
  const [interventionTypesFilter, setInterventionTypesFilter] = useState<FilterType[]>(defaultInterventionTypes);
  const [businessTypesFilter, setBusinessTypesFilter] = useState<FilterType[]>(savedBusinessTypeTypes || businessTypeFilter);
  const stateFilters = usePlanningStateFilter();
  const [interventionStatesFilter, setInterventionStatesFilter] = useState<FilterType[]>(stateFilters);
  const [beginDate, setBeginDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState<undefined | Date>(getCurrentDate());
  const [interventionIds, setInterventionIds] = useState<string[]>([]);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [customerFullName, setCustomerFullName] = useState<string>('');
  const processesQuery = useGetProcessesQuery();

  const setProcessesWithCache = (processesToUpdate: FilterType[]) => {
    setProcesses(processesToUpdate);
    localStorage.setItem(KEY_CACHE_PROCESSES, JSON.stringify(processesToUpdate));
  };
  const processesReady = processes.length > 0;

  useEffect(() => {
    if (processesQuery.data) {
      const noProcessSelected = processes.filter(p => p.selected).length === 0;
      const processesFromRequest = processesQuery.data.map(process => ({
        id: process.id,
        label: process.label,
        selected: noProcessSelected ? true : Boolean(processes.find(p => p.id === process.id)?.selected),
      }));
      setProcessesWithCache(processesFromRequest);
    }
  }, [processesQuery.data]);

  const setInterventionTypesFilterWithCache = (interventionTypesToUpdate: FilterType[]) => {
    setInterventionTypesFilter(interventionTypesToUpdate);
    cache.set(keyCacheInterventionType, interventionTypesToUpdate);
  };

  const setBusinessTypeFilterWithCache = (businessTypesToUpdate: FilterType[]) => {
    setBusinessTypesFilter(businessTypesToUpdate);
    cache.set(keyCacheBusinessType, businessTypesToUpdate);
  };

  const value: InterventionsProviderProps = useMemo(
    () => ({
      processesReady,
      processes,
      interventionStatesFilter,
      interventionTypesFilter,
      businessTypesFilter,
      setProcesses: setProcessesWithCache,
      beginDate,
      setBeginDate,
      endDate,
      setEndDate,
      interventionIds,
      setInterventionIds,
      interventions,
      setInterventions,
      customerFullName,
      setCustomerFullName,
      setInterventionStatesFilter,
      setInterventionTypesFilter: setInterventionTypesFilterWithCache,
      setBusinessTypeTypesFilter: setBusinessTypeFilterWithCache,
    }),
    [
      processes,
      interventionIds,
      interventionStatesFilter,
      businessTypesFilter,
      interventionTypesFilter,
      interventions,
      endDate,
      beginDate,
      customerFullName,
    ]
  );

  return <InterventionContext.Provider value={value}>{children}</InterventionContext.Provider>;
};

export const useInterventionContext = () => {
  const interventionContext = useContext(InterventionContext);

  if (!interventionContext) {
    throw new Error('useInterventionContext must be used inside the InterventionProvider');
  }

  return interventionContext;
};
